/**
 * Every rip. Sorted by multiple, this is also the anomaly view — a player
 * consistently at the top of it is the first thing worth investigating.
 */
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link } from 'react-router-dom'

import { api, type AdminOpen } from '../lib/api'
import { Empty, ErrorNote, Loading, Panel, StatusChip, cents, dateTime, multiple } from '../components/ui'

const SORTS = [
  { key: 'recent', label: 'Newest' },
  { key: 'multiple', label: 'Biggest multiple' },
  { key: 'value', label: 'Biggest payout' },
] as const

export function OpensPage() {
  const [sort, setSort] = useState<(typeof SORTS)[number]['key']>('recent')

  const { data, isLoading, error } = useQuery({
    queryKey: ['opens', sort],
    queryFn: () => api.get<{ opens: AdminOpen[] }>(`/opens?sort=${sort}&limit=100`),
  })

  return (
    <div className="space-y-4">
      <div className="flex gap-1">
        {SORTS.map((s) => (
          <button
            key={s.key}
            className={`btn btn-sm ${sort === s.key ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setSort(s.key)}
          >
            {s.label}
          </button>
        ))}
      </div>

      {error && <ErrorNote>Could not load rips.</ErrorNote>}

      <Panel title="Rips">
        {isLoading ? (
          <Loading />
        ) : !data || data.opens.length === 0 ? (
          <Empty>Nothing opened yet.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className="grid-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>Player</th>
                  <th>Pack</th>
                  <th className="text-right">Paid</th>
                  <th className="text-right">Returned</th>
                  <th className="text-right">Multiple</th>
                  <th>Best pull</th>
                </tr>
              </thead>
              <tbody>
                {data.opens.map((o) => (
                  <tr key={o.id}>
                    <td className="whitespace-nowrap" style={{ color: 'var(--color-muted)' }}>
                      {dateTime(o.openedAt)}
                    </td>
                    <td>
                      <Link
                        to={`/users/${o.user.id}`}
                        className="font-medium hover:underline"
                        style={{ color: 'var(--color-info)' }}
                      >
                        {o.user.username ?? o.user.address.slice(0, 10)}
                      </Link>
                    </td>
                    <td>{o.packName}</td>
                    <td className="tnum text-right">{cents(o.priceCents)}</td>
                    <td className="tnum text-right">{cents(o.totalCents)}</td>
                    <td
                      className="tnum text-right font-semibold"
                      style={{ color: o.multiple >= 1 ? 'var(--color-up)' : 'var(--color-down)' }}
                    >
                      {multiple(o.multiple)}
                    </td>
                    <td>
                      {o.topPull ? (
                        <span className="flex items-center gap-2">
                          <StatusChip status={o.topPull.rarity} />
                          <span className="font-medium">{o.topPull.symbol}</span>
                          <span className="tnum text-xs" style={{ color: 'var(--color-faint)' }}>
                            {cents(o.topPull.valueCents)}
                          </span>
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </div>
  )
}
